import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Calendar, 
  Mail, 
  ArrowRight, 
  FlaskConical, 
  Download,
  Clock,
  Users,
  Loader2,
  Copy,
  Check,
  Printer,
  Sparkles,
  PartyPopper,
  Share2
} from 'lucide-react';
import { EmbedLayout, LandingLayout } from '@/components/layout';
import { useDisplayMode } from '@/hooks/useDisplayMode';
import { usePropertyContext } from '@/context/PropertyContext';
import { postMessageToParent, formatPrice, cn } from '@/lib/utils';
import { WIDGET_MESSAGE_TYPES } from '@/lib/constants';
import { trackBookingCompleted, markBookingCompleted } from '@/lib/analytics';
import { api, type BookingDetails } from '@/lib/api';

/**
 * Generate ICS calendar file content
 */
function generateICSFile(booking: BookingDetails, propertyName: string): string {
  const startDate = new Date(`${booking.date}T${booking.startTime}`);
  const endDate = booking.endTime 
    ? new Date(`${booking.date}T${booking.endTime}`)
    : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

  const formatICSDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  const escapeICS = (str: string) => {
    return str.replace(/[,;\\]/g, '\\$&').replace(/\n/g, '\\n');
  };

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ExperienceLocal//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${booking.id}@experiencelocal.io`,
    `DTSTAMP:${formatICSDate(new Date())}`,
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:${escapeICS(booking.experience.name)}`,
    `DESCRIPTION:${escapeICS(`Booking #${booking.bookingNumber}\\nGuests: ${booking.guestCount}\\nBooked at ${propertyName}`)}`,
    `ORGANIZER;CN=${escapeICS(propertyName)}:mailto:bookings@experiencelocal.io`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.join('\r\n');
}

/**
 * Download ICS file
 */
function downloadICSFile(booking: BookingDetails, propertyName: string) {
  const icsContent = generateICSFile(booking, propertyName);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `booking-${booking.bookingNumber}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Confetti particle for celebration animation
 */
function ConfettiParticle({ delay, color }: { delay: number; color: string }) {
  const randomX = Math.random() * 100;
  const randomRotation = Math.random() * 360;
  const randomDuration = 2 + Math.random() * 2;
  
  return (
    <div
      className="absolute w-2 h-2 rounded-sm pointer-events-none"
      style={{
        left: `${randomX}%`,
        top: '-10px',
        backgroundColor: color,
        transform: `rotate(${randomRotation}deg)`,
        animation: `confetti-fall ${randomDuration}s ease-out ${delay}s forwards`,
        opacity: 0,
      }}
    />
  );
}

/**
 * SuccessPage - Premium booking confirmation with celebration
 * Features elegant design and celebratory animations
 */
export function SuccessPage() {
  const displayMode = useDisplayMode();
  const { propertyKey, config, isDemo } = usePropertyContext();
  const [searchParams] = useSearchParams();
  const hasTracked = useRef(false);
  
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  const bookingNumber = searchParams.get('booking');
  const experienceId = searchParams.get('experienceId');
  const experienceName = searchParams.get('experienceName');
  const amount = searchParams.get('amount');
  const currency = searchParams.get('currency');
  const token = searchParams.get('token');
  
  const isDemoBooking = searchParams.get('demo') === 'true' || isDemo;
  const Layout = displayMode === 'embed' ? EmbedLayout : LandingLayout;
  const homeLink = displayMode === 'embed' ? `/embed/${propertyKey}` : '/';

  // Hide confetti after animation
  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch booking details
  useEffect(() => {
    async function fetchBookingDetails() {
      if (!propertyKey || !bookingNumber || isDemoBooking) return;
      
      setIsLoadingDetails(true);
      try {
        const details = await api.getBookingDetails(propertyKey, bookingNumber, token || undefined);
        setBookingDetails(details);
      } catch (err) {
        console.error('Failed to fetch booking details:', err);
      } finally {
        setIsLoadingDetails(false);
      }
    }

    fetchBookingDetails();
  }, [propertyKey, bookingNumber, token, isDemoBooking]);

  // Track booking completion
  useEffect(() => {
    if (!hasTracked.current && bookingNumber && !isDemoBooking) {
      hasTracked.current = true;
      markBookingCompleted();
      trackBookingCompleted({
        experienceId: bookingDetails?.experience.id || experienceId || 'unknown',
        experienceName: bookingDetails?.experience.name || experienceName || undefined,
        value: bookingDetails?.pricing.totalPrice || (amount ? parseFloat(amount) : undefined),
        currency: bookingDetails?.pricing.currency || currency || 'USD',
        bookingNumber,
      });
    }
  }, [bookingNumber, experienceId, experienceName, amount, currency, isDemoBooking, bookingDetails]);

  // Notify parent of successful booking
  useEffect(() => {
    if (displayMode === 'embed' && bookingNumber) {
      postMessageToParent(WIDGET_MESSAGE_TYPES.BOOKING_COMPLETE, {
        bookingNumber,
        isDemo: isDemoBooking,
      });
    }
  }, [displayMode, bookingNumber, isDemoBooking]);

  const copyBookingNumber = useCallback(async () => {
    if (!bookingNumber) return;
    try {
      await navigator.clipboard.writeText(bookingNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [bookingNumber]);

  const handleCalendarDownload = useCallback(() => {
    if (!bookingDetails || !config?.property.name) return;
    downloadICSFile(bookingDetails, config.property.name);
  }, [bookingDetails, config]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const formatBookingDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatBookingTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Confetti colors
  const confettiColors = ['#b8860b', '#d97706', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

  return (
    <Layout>
      <div className="mx-auto max-w-lg py-12 print:py-4 relative">
        {/* Confetti Animation */}
        {showConfetti && !isDemoBooking && (
          <div className="absolute inset-x-0 top-0 h-64 overflow-hidden pointer-events-none">
            {Array.from({ length: 50 }).map((_, i) => (
              <ConfettiParticle
                key={i}
                delay={Math.random() * 0.5}
                color={confettiColors[i % confettiColors.length]}
              />
            ))}
          </div>
        )}

        {/* Success Icon - Premium animation */}
        <div className={cn(
          'mx-auto mb-8 relative print:hidden',
          'animate-scale-in'
        )}>
          <div className={cn(
            'flex h-20 w-20 items-center justify-center rounded-2xl mx-auto',
            'shadow-luxury',
            isDemoBooking
              ? 'bg-gradient-to-br from-warning/10 to-warning/20'
              : 'bg-gradient-to-br from-success/10 to-success/20'
          )}>
            {isDemoBooking ? (
              <FlaskConical className="h-10 w-10 text-warning" />
            ) : (
              <CheckCircle className="h-10 w-10 text-success" />
            )}
          </div>
          
          {/* Celebration sparkles */}
          {!isDemoBooking && (
            <>
              <Sparkles className="absolute -top-2 -right-2 h-6 w-6 text-primary animate-pulse-subtle" />
              <PartyPopper className="absolute -bottom-1 -left-3 h-5 w-5 text-rating animate-float" />
            </>
          )}
        </div>

        {/* Title - Elegant serif */}
        <h1 className="font-serif text-3xl font-medium text-center text-foreground animate-fade-in">
          {isDemoBooking ? 'Demo Booking Complete!' : 'Booking Confirmed!'}
        </h1>
        <p className="mt-3 text-muted-foreground text-center animate-fade-in" style={{ animationDelay: '100ms' }}>
          {isDemoBooking
            ? 'This was a demo booking. No real reservation was created.'
            : "Thank you for your booking. We're excited to see you!"
          }
        </p>

        {/* Demo Mode Notice */}
        {isDemoBooking && (
          <div className="mt-8 rounded-2xl border border-warning/30 bg-gradient-to-r from-warning/5 to-warning/10 p-5 text-left print:hidden shadow-soft animate-slide-up">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-warning/15 shrink-0">
                <FlaskConical className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="font-semibold text-warning-foreground">Demo Mode</p>
                <p className="mt-1 text-sm text-warning-foreground/80 leading-relaxed">
                  This booking was created in demo mode. No payment was processed and no actual reservation was made.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Booking Number - Premium card */}
        {bookingNumber && (
          <div
            className={cn(
              'mt-8 rounded-2xl p-5 shadow-soft animate-slide-up',
              isDemoBooking
                ? 'bg-gradient-to-r from-warning/5 to-warning/10 border border-warning/30'
                : 'bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20'
            )}
            style={{ animationDelay: '150ms' }}
          >
            <p className={cn(
              'text-xs font-semibold uppercase tracking-elegant text-center',
              isDemoBooking ? 'text-warning-foreground' : 'text-primary'
            )}>
              {isDemoBooking ? 'Demo Booking Reference' : 'Booking Reference'}
            </p>
            <div className="mt-2 flex items-center justify-center gap-3">
              <p className={cn(
                'font-mono text-2xl font-bold tracking-wide',
                isDemoBooking ? 'text-amber-800' : 'text-foreground'
              )}>
                {bookingNumber}
              </p>
              <button
                onClick={copyBookingNumber}
                className={cn(
                  'rounded-lg p-2 transition-all print:hidden',
                  'hover:bg-black/5',
                  copied && 'bg-emerald-100'
                )}
                aria-label="Copy booking number"
              >
                {copied ? (
                  <Check className="h-5 w-5 text-emerald-600" />
                ) : (
                  <Copy className="h-5 w-5 text-muted-foreground" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Booking Details */}
        {isLoadingDetails && (
          <div className="mt-8 flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading booking details...</span>
          </div>
        )}

        {bookingDetails && !isDemoBooking && (
          <div 
            className="mt-8 rounded-2xl border border-border/50 bg-card shadow-luxury overflow-hidden animate-slide-up"
            style={{ animationDelay: '200ms' }}
          >
            {/* Experience Info */}
            <div className="flex gap-4 p-5 bg-gradient-to-r from-muted/50 to-muted/20 border-b border-border/30">
              {bookingDetails.experience.coverImage && (
                <img
                  src={bookingDetails.experience.coverImage}
                  alt={bookingDetails.experience.name}
                  className="h-18 w-18 rounded-xl object-cover shadow-sm shrink-0"
                />
              )}
              <div className="min-w-0">
                <h3 className="font-serif text-lg font-medium text-foreground truncate">{bookingDetails.experience.name}</h3>
                <p className="text-sm text-muted-foreground">{bookingDetails.property.name}</p>
              </div>
            </div>

            {/* Booking Info */}
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/50">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                </div>
                <span className="text-foreground font-medium">{formatBookingDate(bookingDetails.date)}</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/50">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                </div>
                <span className="text-foreground font-medium">
                  {formatBookingTime(bookingDetails.startTime)}
                  {bookingDetails.endTime && ` - ${formatBookingTime(bookingDetails.endTime)}`}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/50">
                  <Users className="h-5 w-5 text-muted-foreground" />
                </div>
                <span className="text-foreground font-medium">{bookingDetails.guestCount} {bookingDetails.guestCount === 1 ? 'guest' : 'guests'}</span>
              </div>
            </div>

            {/* Price */}
            <div className="p-5 border-t border-border/30 bg-gradient-to-r from-primary/5 to-primary/10">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Paid</span>
                <span className="price-display text-foreground">
                  {formatPrice(bookingDetails.pricing.totalPrice, bookingDetails.pricing.currency)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Next Steps */}
        {!isDemoBooking && (
          <div className="mt-8 space-y-3 text-left print:hidden animate-slide-up" style={{ animationDelay: '250ms' }}>
            <div className="flex items-start gap-4 rounded-xl border border-border/50 bg-card p-4 shadow-soft">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Confirmation Email</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  We&apos;ve sent a confirmation email with your booking details.
                </p>
              </div>
            </div>

            {bookingDetails && (
              <button
                onClick={handleCalendarDownload}
                className="w-full flex items-start gap-4 rounded-xl border border-border/50 bg-card p-4 text-left shadow-soft hover:bg-muted/30 transition-colors"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-foreground">Add to Calendar</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Download calendar invite (.ics)
                  </p>
                </div>
                <Download className="h-5 w-5 text-muted-foreground mt-2" />
              </button>
            )}
          </div>
        )}

        {/* Actions - Premium buttons */}
        <div className="mt-10 flex flex-col gap-3 print:hidden animate-slide-up" style={{ animationDelay: '300ms' }}>
          <Link
            to={homeLink}
            className="btn-luxury w-full justify-center text-base"
          >
            {isDemoBooking ? 'Try Another Demo Booking' : 'Explore More Experiences'}
            <ArrowRight className="h-5 w-5" />
          </Link>

          <div className="flex gap-3">
            {!isDemoBooking && (
              <button
                onClick={handlePrint}
                className="btn-luxury-secondary flex-1 justify-center"
              >
                <Printer className="h-4 w-4" />
                Print
              </button>
            )}

            {config?.property.contact?.website && (
              <a
                href={config.property.contact.website}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'btn-luxury-secondary justify-center',
                  !isDemoBooking ? 'flex-1' : 'w-full'
                )}
              >
                <Share2 className="h-4 w-4" />
                Visit {config.property.name}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* CSS for confetti animation */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(400px) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </Layout>
  );
}
